/**
 * Zone Management Service
 *
 * Handles CRUD operations for store zones (heatmap customization).
 * Allows Managers and Super Admins to customize their Zone Heatmap
 * by adding, editing, and removing zones.
 *
 * Zone configurations are persisted per store and per user role:
 * - Super Admin: Can manage zones for any store
 * - Regional Manager: Can manage zones for assigned stores
 * - Store Manager: Can manage zones for their store only
 */

import { supabase } from './supabase';
import type { StoreZone, ZoneKPI, ActivityLog } from '../types';
import type { IconName } from '../components/ui/Icon';

// ============================================
// TYPES
// ============================================

export type ZoneCategory =
  | 'entrance'
  | 'checkout'
  | 'backroom'
  | 'sales_floor'
  | 'warehouse'
  | 'receiving'
  | 'office'
  | 'custom';

export interface ZoneConfig {
  id: string;
  storeId: string;
  tenantId: string;
  name: string;
  category: ZoneCategory;
  gridPosition: {
    row: string;
    col: string;
  };
  isCustom: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateZoneInput {
  name: string;
  category: ZoneCategory;
  gridPosition: {
    row: string;
    col: string;
  };
}

export interface UpdateZoneInput {
  name?: string;
  category?: ZoneCategory;
  gridPosition?: {
    row: string;
    col: string;
  };
}

export interface ZoneWithMetrics extends ZoneConfig {
  riskScore: number;
  topIssue: string;
  kpis: ZoneKPI[];
  staffOnDuty: number;
  cameraStatus: 'nominal' | 'warning' | 'error';
  activeIncidents: number;
  recentActivity: ActivityLog[];
  suggestedActions: string[];
}

// ============================================
// ZONE OVERLAP UTILITIES
// ============================================

/**
 * Parse a CSS grid position string (e.g., "1 / 3") into start and end values.
 * Grid positions use CSS syntax: "start / end"
 */
export function parseGridPosition(position: string): { start: number; end: number } {
  const parts = position.split('/').map(p => parseInt(p.trim(), 10));
  return {
    start: parts[0] || 1,
    end: parts[1] || parts[0] + 1,
  };
}

/**
 * Check if two ranges overlap.
 * Two ranges [a1, a2) and [b1, b2) overlap if a1 < b2 AND b1 < a2
 */
function rangesOverlap(start1: number, end1: number, start2: number, end2: number): boolean {
  return start1 < end2 && start2 < end1;
}

/**
 * Check if two grid positions overlap.
 * Returns true if the zones occupy any of the same grid cells.
 */
export function zonesOverlap(
  position1: { row: string; col: string },
  position2: { row: string; col: string }
): boolean {
  const row1 = parseGridPosition(position1.row);
  const col1 = parseGridPosition(position1.col);
  const row2 = parseGridPosition(position2.row);
  const col2 = parseGridPosition(position2.col);

  // Zones overlap if both rows AND columns overlap
  return rangesOverlap(row1.start, row1.end, row2.start, row2.end) &&
         rangesOverlap(col1.start, col1.end, col2.start, col2.end);
}

/**
 * Find any zones that overlap with a given position.
 * Useful for validation before creating or updating a zone.
 *
 * @param existingZones - Array of existing zones to check against
 * @param position - The position to check for overlaps
 * @param excludeZoneId - Optional zone ID to exclude (for update scenarios)
 * @returns Array of zones that overlap with the given position
 */
export function findOverlappingZones(
  existingZones: ZoneConfig[],
  position: { row: string; col: string },
  excludeZoneId?: string
): ZoneConfig[] {
  return existingZones.filter(zone => {
    // Skip the zone being updated
    if (excludeZoneId && zone.id === excludeZoneId) {
      return false;
    }
    return zonesOverlap(zone.gridPosition, position);
  });
}

export interface ZoneValidationResult {
  valid: boolean;
  error?: string;
  overlappingZones?: ZoneConfig[];
}

// ============================================
// DEFAULT ZONE TEMPLATES
// ============================================

export const DEFAULT_ZONE_TEMPLATES: Record<ZoneCategory, { defaultName: string; icon: IconName; suggestedActions: string[] }> = {
  entrance: {
    defaultName: 'Entrada Principal',
    icon: 'arrow-right',
    suggestedActions: ['Verificar conteo de visitantes', 'Revisar cámaras de seguridad'],
  },
  checkout: {
    defaultName: 'Cajas',
    icon: 'briefcase',
    suggestedActions: ['Auditar transacciones', 'Verificar procesos de devolución'],
  },
  backroom: {
    defaultName: 'Bodega',
    icon: 'package',
    suggestedActions: ['Conteo de inventario', 'Verificar recepción de mercadería'],
  },
  sales_floor: {
    defaultName: 'Piso de Ventas',
    icon: 'store',
    suggestedActions: ['Verificar disponibilidad', 'Revisar merma visible'],
  },
  warehouse: {
    defaultName: 'Almacén',
    icon: 'building',
    suggestedActions: ['Auditoría de picking', 'Verificar condiciones de almacenamiento'],
  },
  receiving: {
    defaultName: 'Recepción',
    icon: 'download',
    suggestedActions: ['Verificar órdenes de compra', 'Auditar recepción de mercadería'],
  },
  office: {
    defaultName: 'Oficinas',
    icon: 'building',
    suggestedActions: ['Revisar documentación', 'Verificar accesos'],
  },
  custom: {
    defaultName: 'Zona Personalizada',
    icon: 'map-pin',
    suggestedActions: ['Definir procedimientos', 'Establecer métricas'],
  },
};

// ============================================
// SERVICE
// ============================================

export const zoneManagementService = {
  /**
   * Get all zones for a store.
   * Returns empty array when no zones exist (does NOT fall back to mock data).
   */
  getZones: async (storeId: string, tenantId: string): Promise<ZoneConfig[]> => {
    try {
      const { data, error } = await supabase
        .from('store_zones')
        .select('*')
        .eq('store_id', storeId)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: true });

      if (error) {
        // Log specific error code for debugging
        console.error(`[zoneManagementService.getZones] Error (code: ${error.code}):`, error.message);
        throw error;
      }

      // Return empty array if no zones exist - this is valid, not an error
      if (!data || data.length === 0) {
        console.debug(`[zoneManagementService.getZones] No zones configured for store ${storeId}`);
        return [];
      }

      return data.map((row: any) => ({
        id: row.id,
        storeId: row.store_id,
        tenantId: row.tenant_id,
        name: row.name,
        category: row.category as ZoneCategory,
        gridPosition: row.grid_position || { row: '1 / 2', col: '1 / 2' },
        isCustom: row.is_custom || false,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at || row.created_at,
      }));
    } catch (e) {
      console.error('[zoneManagementService.getZones] Error:', e);
      return [];
    }
  },

  /**
   * Get a single zone by ID.
   */
  getZone: async (zoneId: string): Promise<ZoneConfig | null> => {
    try {
      const { data, error } = await supabase
        .from('store_zones')
        .select('*')
        .eq('id', zoneId)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        storeId: data.store_id,
        tenantId: data.tenant_id,
        name: data.name,
        category: data.category as ZoneCategory,
        gridPosition: (data.grid_position as { row: string; col: string }) || { row: '1 / 2', col: '1 / 2' },
        isCustom: data.is_custom || false,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at || data.created_at,
      };
    } catch (e) {
      console.warn('Failed to fetch zone:', e);
      return null;
    }
  },

  /**
   * Validate that a zone position doesn't overlap with existing zones.
   * Returns validation result with error message if overlapping zones found.
   */
  validateZonePosition: async (
    storeId: string,
    tenantId: string,
    position: { row: string; col: string },
    excludeZoneId?: string
  ): Promise<ZoneValidationResult> => {
    const existingZones = await zoneManagementService.getZones(storeId, tenantId);
    const overlapping = findOverlappingZones(existingZones, position, excludeZoneId);

    if (overlapping.length > 0) {
      const zoneNames = overlapping.map(z => z.name).join(', ');
      return {
        valid: false,
        error: `La zona se superpone con: ${zoneNames}`,
        overlappingZones: overlapping,
      };
    }

    return { valid: true };
  },

  /**
   * Create a new zone for a store.
   * Validates that the zone position doesn't overlap with existing zones.
   */
  createZone: async (
    storeId: string,
    tenantId: string,
    userId: string,
    input: CreateZoneInput
  ): Promise<ZoneConfig | null> => {
    // Validate position doesn't overlap with existing zones
    const validation = await zoneManagementService.validateZonePosition(
      storeId,
      tenantId,
      input.gridPosition
    );

    if (!validation.valid) {
      console.error('[zoneManagementService.createZone] Zone overlap detected:', validation.error);
      throw new Error(validation.error);
    }

    try {
      const { data, error } = await supabase
        .from('store_zones')
        .insert({
          store_id: storeId,
          tenant_id: tenantId,
          name: input.name,
          category: input.category,
          grid_position: input.gridPosition,
          is_custom: true,
          created_by: userId,
        })
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        storeId: data.store_id,
        tenantId: data.tenant_id,
        name: data.name,
        category: data.category as ZoneCategory,
        gridPosition: (data.grid_position as { row: string; col: string }) || { row: '1 / 2', col: '1 / 2' },
        isCustom: data.is_custom || false,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at || data.created_at,
      };
    } catch (e) {
      // If it's an overlap error, re-throw it
      if (e instanceof Error && e.message.includes('superpone')) {
        throw e;
      }

      console.error('[zoneManagementService.createZone] Error:', e);
      throw e;
    }
  },

  /**
   * Update an existing zone.
   * Validates that the new position doesn't overlap with existing zones.
   */
  updateZone: async (zoneId: string, input: UpdateZoneInput): Promise<ZoneConfig | null> => {
    // If updating grid position, validate it doesn't overlap
    if (input.gridPosition) {
      // Get current zone to know storeId and tenantId
      const currentZone = await zoneManagementService.getZone(zoneId);
      if (currentZone) {
        const validation = await zoneManagementService.validateZonePosition(
          currentZone.storeId,
          currentZone.tenantId,
          input.gridPosition,
          zoneId // Exclude current zone from overlap check
        );

        if (!validation.valid) {
          console.error('[zoneManagementService.updateZone] Zone overlap detected:', validation.error);
          throw new Error(validation.error);
        }
      }
    }

    try {
      const updateData: Record<string, unknown> = {};

      if (input.name !== undefined) updateData.name = input.name;
      if (input.category !== undefined) updateData.category = input.category;
      if (input.gridPosition !== undefined) updateData.grid_position = input.gridPosition;

      const { data, error } = await supabase
        .from('store_zones')
        .update(updateData)
        .eq('id', zoneId)
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        storeId: data.store_id,
        tenantId: data.tenant_id,
        name: data.name,
        category: data.category as ZoneCategory,
        gridPosition: (data.grid_position as { row: string; col: string }) || { row: '1 / 2', col: '1 / 2' },
        isCustom: data.is_custom || false,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at || data.created_at,
      };
    } catch (e) {
      // If it's an overlap error, re-throw it
      if (e instanceof Error && e.message.includes('superpone')) {
        throw e;
      }

      console.error('[zoneManagementService.updateZone] Error:', e);
      throw e;
    }
  },

  /**
   * Delete a zone.
   */
  deleteZone: async (zoneId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('store_zones')
        .delete()
        .eq('id', zoneId);

      if (error) throw error;
      return true;
    } catch (e) {
      console.error('[zoneManagementService.deleteZone] Error:', e);
      return false;
    }
  },

  /**
   * Get zones with live metrics (combines zone config with real-time data).
   * This is what the heatmap uses to display zones with risk scores.
   */
  getZonesWithMetrics: async (
    storeId: string,
    tenantId: string
  ): Promise<ZoneWithMetrics[]> => {
    try {
      // Get zone configurations
      const zones = await zoneManagementService.getZones(storeId, tenantId);

      // Get zone metrics (would come from real-time data source in production)
      const { data: metricsData, error: metricsError } = await supabase
        .from('zone_metrics')
        .select('*')
        .eq('store_id', storeId)
        .in('zone_id', zones.map(z => z.id));

      if (metricsError) {
        console.warn('Failed to fetch zone metrics, using defaults:', metricsError);
      }

      const metricsMap = new Map(
        (metricsData || []).map((m: any) => [m.zone_id, m])
      );

      // Combine zone config with metrics
      return zones.map(zone => {
        const metrics = metricsMap.get(zone.id);
        const template = DEFAULT_ZONE_TEMPLATES[zone.category];

        return {
          ...zone,
          riskScore: metrics?.risk_score ?? 0.15,
          topIssue: metrics?.top_issue ?? 'Normal',
          kpis: metrics?.kpis ?? [],
          staffOnDuty: metrics?.staff_on_duty ?? 0,
          cameraStatus: metrics?.camera_status ?? 'nominal',
          activeIncidents: metrics?.active_incidents ?? 0,
          recentActivity: metrics?.recent_activity ?? [],
          suggestedActions: template.suggestedActions,
        };
      });
    } catch (e) {
      console.error('Failed to fetch zones with metrics:', e);
      return [];
    }
  },

  /**
   * Initialize default zones for a new store.
   */
  initializeDefaultZones: async (
    storeId: string,
    tenantId: string,
    userId: string
  ): Promise<ZoneConfig[]> => {
    const defaultZones: CreateZoneInput[] = [
      { name: 'Entrada Principal', category: 'entrance', gridPosition: { row: '1 / 2', col: '1 / 3' } },
      { name: 'Cajas 1-4', category: 'checkout', gridPosition: { row: '1 / 2', col: '4 / 7' } },
      { name: 'Frutas y Verduras', category: 'sales_floor', gridPosition: { row: '2 / 3', col: '1 / 3' } },
      { name: 'Lácteos y Refrigerados', category: 'sales_floor', gridPosition: { row: '2 / 3', col: '3 / 5' } },
      { name: 'Electrónica', category: 'sales_floor', gridPosition: { row: '2 / 3', col: '5 / 7' } },
      { name: 'Bodega Principal', category: 'backroom', gridPosition: { row: '3 / 4', col: '1 / 4' } },
      { name: 'Recepción', category: 'receiving', gridPosition: { row: '3 / 4', col: '4 / 7' } },
    ];

    const createdZones: ZoneConfig[] = [];

    for (const input of defaultZones) {
      const zone = await zoneManagementService.createZone(storeId, tenantId, userId, input);
      if (zone) {
        createdZones.push({ ...zone, isCustom: false });
      }
    }

    return createdZones;
  },

  /**
   * Clone zones from one store to another.
   */
  cloneZones: async (
    sourceStoreId: string,
    targetStoreId: string,
    tenantId: string,
    userId: string
  ): Promise<ZoneConfig[]> => {
    const sourceZones = await zoneManagementService.getZones(sourceStoreId, tenantId);
    const clonedZones: ZoneConfig[] = [];

    for (const zone of sourceZones) {
      const cloned = await zoneManagementService.createZone(
        targetStoreId,
        tenantId,
        userId,
        {
          name: zone.name,
          category: zone.category,
          gridPosition: zone.gridPosition,
        }
      );
      if (cloned) {
        clonedZones.push(cloned);
      }
    }

    return clonedZones;
  },

  /**
   * Get available zone categories.
   */
  getCategories: (): Array<{ id: ZoneCategory; name: string; icon: IconName }> => {
    return Object.entries(DEFAULT_ZONE_TEMPLATES).map(([id, template]) => ({
      id: id as ZoneCategory,
      name: template.defaultName,
      icon: template.icon,
    }));
  },

  /**
   * Detect inventory locations that don't have a matching zone.
   * Compares distinct inventory.location values against store_zones.name.
   * @param storeId - The store UUID
   * @param tenantId - The tenant UUID (required for security)
   * @returns Array of location names that exist in inventory but not in zones
   */
  detectUnmappedLocations: async (
    storeId: string,
    tenantId: string
  ): Promise<string[]> => {
    if (!tenantId || !storeId) {
      console.error('[zoneManagementService.detectUnmappedLocations] tenantId and storeId are required');
      return [];
    }

    try {
      // Get existing zone names for this store
      const existingZones = await zoneManagementService.getZones(storeId, tenantId);
      const existingZoneNames = new Set(existingZones.map(z => z.name.toLowerCase().trim()));

      // Get distinct locations from inventory for this store
      const { data: inventoryLocations, error } = await supabase
        .from('inventory')
        .select('location')
        .eq('tenant_id', tenantId)
        .eq('store_id', storeId)
        .not('location', 'is', null)
        .not('location', 'eq', '');

      if (error) {
        console.error('[zoneManagementService.detectUnmappedLocations] Error fetching inventory locations:', error);
        return [];
      }

      // Get unique locations that don't exist as zones
      const uniqueLocations = new Set<string>();
      (inventoryLocations || []).forEach((row: { location: string | null }) => {
        if (row.location && row.location.trim()) {
          const locationName = row.location.trim();
          if (!existingZoneNames.has(locationName.toLowerCase())) {
            uniqueLocations.add(locationName);
          }
        }
      });

      return Array.from(uniqueLocations).sort();
    } catch (e) {
      console.error('[zoneManagementService.detectUnmappedLocations] Unexpected error:', e);
      return [];
    }
  },

  /**
   * Calculate auto positions for new zones in the grid.
   * Uses a bin-packing algorithm to find available 1x1 slots.
   * @param count - Number of positions needed
   * @param existingZones - Current zones to avoid overlapping
   * @returns Array of grid positions for new zones
   */
  calculateAutoPositions: (
    count: number,
    existingZones: ZoneConfig[]
  ): Array<{ row: string; col: string }> => {
    const GRID_COLS = 6;
    const GRID_ROWS = 10;

    // Build occupancy matrix (true = occupied)
    const occupied: boolean[][] = Array.from({ length: GRID_ROWS }, () =>
      Array(GRID_COLS).fill(false)
    );

    // Mark existing zone positions as occupied
    existingZones.forEach(zone => {
      const row = parseGridPosition(zone.gridPosition.row);
      const col = parseGridPosition(zone.gridPosition.col);

      for (let r = row.start - 1; r < row.end - 1 && r < GRID_ROWS; r++) {
        for (let c = col.start - 1; c < col.end - 1 && c < GRID_COLS; c++) {
          if (r >= 0 && c >= 0) {
            occupied[r][c] = true;
          }
        }
      }
    });

    // Find available 1x1 slots (row by row, left to right)
    const positions: Array<{ row: string; col: string }> = [];
    for (let r = 0; r < GRID_ROWS && positions.length < count; r++) {
      for (let c = 0; c < GRID_COLS && positions.length < count; c++) {
        if (!occupied[r][c]) {
          positions.push({
            row: `${r + 1} / ${r + 2}`,
            col: `${c + 1} / ${c + 2}`,
          });
          occupied[r][c] = true;
        }
      }
    }

    return positions;
  },

  /**
   * Batch create zones from detected inventory locations.
   * Auto-assigns grid positions and uses 'sales_floor' category by default.
   * @param storeId - The store UUID
   * @param tenantId - The tenant UUID
   * @param userId - The user creating the zones
   * @param locations - Array of location names to create zones for
   * @param _existingZones - Ignored, we fetch fresh from DB for accuracy
   * @returns Array of created zone configs
   */
  createZonesFromLocations: async (
    storeId: string,
    tenantId: string,
    userId: string,
    locations: string[],
    _existingZones: ZoneConfig[]
  ): Promise<ZoneConfig[]> => {
    if (!tenantId || !storeId || !userId) {
      console.error('[zoneManagementService.createZonesFromLocations] tenantId, storeId, and userId are required');
      return [];
    }

    if (locations.length === 0) {
      return [];
    }

    // Fetch current zones from DB to ensure we have accurate state
    // This is more reliable than using the passed existingZones which may be stale
    let currentZones = await zoneManagementService.getZones(storeId, tenantId);
    const createdZones: ZoneConfig[] = [];

    // Create zones one by one, calculating position for each based on current state
    for (const locationName of locations) {
      // Calculate next available position based on current state (including newly created zones)
      const positions = zoneManagementService.calculateAutoPositions(1, currentZones);
      const position = positions[0];

      if (!position) {
        console.warn(`[zoneManagementService.createZonesFromLocations] No position available for zone "${locationName}"`);
        continue;
      }

      try {
        const zone = await zoneManagementService.createZone(
          storeId,
          tenantId,
          userId,
          {
            name: locationName,
            category: 'sales_floor', // Default category for auto-detected zones
            gridPosition: position,
          }
        );

        if (zone) {
          createdZones.push(zone);
          // Add the newly created zone to our local list for next iteration
          currentZones = [...currentZones, zone];
        }
      } catch (error) {
        console.warn(`[zoneManagementService.createZonesFromLocations] Failed to create zone "${locationName}":`, error);
        // Continue with other zones
      }
    }

    return createdZones;
  },

  /**
   * Batch update zone positions (for drag-and-drop reordering).
   * Swaps positions between two zones atomically.
   * @param tenantId - The tenant UUID (required for security)
   * @param updates - Array of zone ID and new position pairs
   * @returns true if all updates succeeded
   */
  updateZonePositions: async (
    tenantId: string,
    updates: Array<{ zoneId: string; gridPosition: { row: string; col: string } }>
  ): Promise<boolean> => {
    if (!tenantId) {
      console.error('[zoneManagementService.updateZonePositions] tenantId is required');
      return false;
    }

    if (updates.length === 0) {
      return true;
    }

    try {
      // Try RPC function first for atomic update
      const { error: rpcError } = await supabase.rpc('update_zone_positions', {
        p_tenant_id: tenantId,
        p_updates: JSON.stringify(updates.map(u => ({
          zone_id: u.zoneId,
          grid_position: u.gridPosition,
        }))),
      });

      if (!rpcError) {
        return true;
      }

      // Fallback to individual updates if RPC not available
      if (rpcError.code === '42883' || rpcError.message?.includes('does not exist')) {
        console.warn('[zoneManagementService.updateZonePositions] RPC not available, using fallback');

        for (const update of updates) {
          const { error } = await supabase
            .from('store_zones')
            .update({
              grid_position: update.gridPosition,
              updated_at: new Date().toISOString(),
            })
            .eq('id', update.zoneId)
            .eq('tenant_id', tenantId);

          if (error) {
            console.error(`[zoneManagementService.updateZonePositions] Failed to update zone ${update.zoneId}:`, error);
            return false;
          }
        }
        return true;
      }

      console.error('[zoneManagementService.updateZonePositions] RPC error:', rpcError);
      return false;
    } catch (e) {
      console.error('[zoneManagementService.updateZonePositions] Unexpected error:', e);
      return false;
    }
  },
};

export default zoneManagementService;
